<script setup lang="ts">
import { site } from '~/data/site'
import type { Project } from '~/types/portfolio'

definePageMeta({
  key: route => route.fullPath
})

type ApiResponse<T> = T | { data?: T }
type ProjectDetailResponse = ApiResponse<Project | Project[]>

const route = useRoute()
const portfolioStore = usePortfolioStore()
const routeParam = String(route.params.slug)

function unwrap<T>(response: ApiResponse<T> | null | undefined): T | undefined {
  if (!response) return undefined
  return typeof response === 'object' && 'data' in response
    ? response.data
    : response
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item).trim())
      .filter(Boolean)
  }

  if (typeof value === 'string' && value.trim()) return [value.trim()]
  return []
}

function normalizeProjectLinks(value: unknown): Project['links'] {
  if (!Array.isArray(value)) return []

  return value.flatMap(item => {
    if (!item || typeof item !== 'object') return []

    const link = String((item as { link?: unknown }).link ?? '').trim()
    const label = String((item as { label?: unknown }).label ?? '').trim().toLowerCase()
    if (!/^https?:\/\//i.test(link) || !['site', 'github'].includes(label)) return []

    return [{
      id: String((item as { id?: unknown }).id ?? '') || undefined,
      label,
      link
    }]
  })
}

function normalizeProjectMedia(value: unknown): Project['media'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const item = value as Record<string, unknown>
  const url = String(item.url ?? '').trim()
  if (!url) return undefined

  const width = Number(item.width)
  const height = Number(item.height)

  return {
    id: String(item.id ?? '') || undefined,
    title: String(item.title ?? '') || undefined,
    alt: String(item.alt ?? '') || undefined,
    url,
    width: Number.isFinite(width) && width > 0 ? width : undefined,
    height: Number.isFinite(height) && height > 0 ? height : undefined
  }
}

function normalizeProject(value: Project): Project {
  const createdYear = typeof value.created_at === 'string'
    ? value.created_at.slice(0, 4)
    : ''

  return {
    ...value,
    slug: String(value.slug ?? ''),
    title: String(value.title ?? ''),
    summary: String(value.summary ?? ''),
    description: toStringArray(value.description),
    tags: toStringArray(value.tags),
    links: normalizeProjectLinks(value.links),
    media: normalizeProjectMedia(value.media),
    year: String(value.year ?? createdYear),
    role: String(value.role ?? ''),
    // banner/gallery ممکن است آبجکت رسانه باشند؛ String() روی آن‌ها
    // مقدار "[object Object]" می‌ساخت.
    banner: resolveMediaUrl(value.banner) ?? '',
    gallery: Array.isArray(value.gallery)
      ? value.gallery
        .map(item => resolveMediaUrl(item))
        .filter((item): item is string => Boolean(item))
      : value.gallery
  }
}

function normalizeProjectList(response: ApiResponse<Project[]> | null): Project[] {
  const projects = unwrap(response)
  return Array.isArray(projects) ? projects.map(normalizeProject) : []
}

function normalizeProjectDetail(response: ProjectDetailResponse | null): Project | undefined {
  const result = unwrap(response)
  const project = Array.isArray(result) ? result[0] : result
  return project ? normalizeProject(project) : undefined
}

/**
 * پاسخ‌های خالی یا پیچیده‌شده‌ی غیرمنتظره هم یک آبجکت معتبر تولید می‌کنند؛
 * بدون این بررسی، قالب با محتوای خالی رندر می‌شود.
 */
function isUsableProject(project: Project | undefined): project is Project {
  if (!project) return false
  return Boolean(project.title || project.summary || project.description.length)
}

const apiOptions = {
  server: true,
  lazy: false,
  storage: 'local' as const,
  storageTtl: 10 * 60 * 1000,
  queue: false
}

// همان کلید صفحه‌ی اصلی؛ این‌طور در ناوبری سمت کلاینت از payload سرور استفاده می‌شود.
const projectListRequest = useApi<ApiResponse<Project[]>>(
  '/v1/general/projects',
  'projects',
  apiOptions
)
const directProjectRequest = useApi<ProjectDetailResponse>(
  `/v1/general/projects/${encodeURIComponent(routeParam)}`,
  `project:${routeParam}`,
  apiOptions
)

await Promise.all([projectListRequest, directProjectRequest])

if (projectListRequest.error.value && directProjectRequest.error.value) {
  throw createError({
    statusCode: 503,
    statusMessage: 'دریافت پروژه‌ها در حال حاضر ممکن نیست'
  })
}

const projectList = normalizeProjectList(projectListRequest.data.value)
if (!projectListRequest.error.value) {
  portfolioStore.projects = projectList
}

const projectSummary = projectList.find(item =>
  item.slug === routeParam || String(item.id) === routeParam
)
let project = normalizeProjectDetail(directProjectRequest.data.value)

if (!isUsableProject(project) && !projectSummary) {
  throw createError({
    statusCode: 404,
    statusMessage: 'پروژه پیدا نشد'
  })
}

const detailProject = isUsableProject(project) ? project : undefined
const canonicalParam = detailProject?.slug
  || projectSummary?.slug
  || String(detailProject?.id ?? projectSummary?.id ?? '')
  || routeParam
const canonicalPath = `/projects/${encodeURIComponent(canonicalParam)}`

// مقایسه روی پارامتر decode‌شده است؛ مقایسه‌ی مسیر خام برای اسلاگ‌های فارسی
// (که در آدرس percent-encoded می‌شوند) ریدایرکت الکی وسط setup می‌ساخت.
if (canonicalParam !== routeParam) {
  await navigateTo(canonicalPath, {
    redirectCode: 301,
    replace: true
  })
}

if (!isUsableProject(project)) {
  const projectId = projectSummary!.id ?? projectSummary!.slug
  const projectRequest = useApi<ProjectDetailResponse>(
    `/v1/general/projects/${encodeURIComponent(String(projectId))}`,
    `project:${projectId}`,
    apiOptions
  )

  await projectRequest

  project = normalizeProjectDetail(projectRequest.data.value)
}

// اگر جزئیات کامل نیامد، همان داده‌ی لیست را نشان بده تا صفحه خالی نماند.
if (!isUsableProject(project) && isUsableProject(projectSummary)) {
  project = projectSummary
}

if (!isUsableProject(project)) {
  throw createError({
    statusCode: 404,
    statusMessage: 'جزئیات پروژه پیدا نشد'
  })
}

const profile = portfolioStore.profile as {
  name?: string
  fullname?: string
  profile?: { name?: string }
} | null
const ownerName = profile?.profile?.name
  || profile?.fullname?.trim()
  || profile?.name
  || site.name
const description = [project.summary, project.description[0]]
  .filter(Boolean)
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, 160)

const requestUrl = useRequestURL({ xForwardedHost: true, xForwardedProto: true })
const origin = requestUrl.hostname === 'localhost'
  ? site.url
  : requestUrl.origin
const canonicalUrl = origin + canonicalPath
const imageUrl = resolveAbsoluteMediaUrl(project.banner, origin)
const createdDate = project.created_at || project.year
const validCreatedDate = /^(19|20)\d{2}(?:-\d{2}(?:-\d{2})?)?/.test(createdDate)

useSeo({
  title: `${project.title} | ${ownerName}`,
  description,
  path: canonicalPath,
  image: imageUrl,
  type: 'article',
  siteName: ownerName,
  imageAlt: project.title,
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: project.title,
      description,
      ...(imageUrl ? { image: imageUrl } : {}),
      url: canonicalUrl,
      inLanguage: 'fa',
      keywords: project.tags.join(', '),
      ...(validCreatedDate ? { dateCreated: createdDate } : {}),
      author: {
        '@type': 'Person',
        name: ownerName,
        url: origin
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'خانه',
          item: origin + '/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: project.title,
          item: canonicalUrl
        }
      ]
    }
  ]
})
</script>

<template>
  <TemplateHost view="detail" :project="project" />
</template>
